import { useMemo, useRef, useState } from 'react';
import { Download, Search } from 'lucide-react';
import type { ProductItem, RankItem } from '@contracts';
import { exportExcel, exportImage, exportPdf } from '@/lib/exportData';

// Medallas top-3 (oro/plata/bronce), como el dashboard original.
const MEDAL = ['from-amber-400 to-yellow-500', 'from-slate-300 to-slate-400', 'from-orange-400 to-amber-600'];
const SHOW = [10, 50, 100, 0]; // 0 = Todos

const money = (v: number) =>
  v.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });
const units = (v: number) => `${Math.round(v).toLocaleString('es-MX')} pzas`;

type Variant = 'rank' | 'product';
type Item = RankItem | ProductItem;
type SortKey = 'value' | 'count' | 'name';

/** Lista con medallas + barra + toolbar. `variant='rank'` (Vendedores/Sucursales:
 *  $ + órdenes) o `variant='product'` (Top productos: $ + piezas). */
export function RankListWidget({
  data,
  title,
  onRowDrill,
  variant = 'rank',
}: {
  data: Item[];
  title?: string;
  /** Solo rank: fila clickable → drill-down de órdenes. */
  onRowDrill?: (item: RankItem) => void;
  variant?: Variant;
}) {
  const isProd = variant === 'product';
  const primary = (it: Item) => (isProd ? (it as ProductItem).total : (it as RankItem).value); // $ a la derecha
  const count = (it: Item) => (isProd ? (it as ProductItem).qty : (it as RankItem).orders); // sub-número
  const countLabel = isProd ? 'piezas' : 'órdenes';
  const defaultSort: SortKey = isProd ? 'count' : 'value';

  const [q, setQ] = useState('');
  const [limit, setLimit] = useState(10);
  const [menuOpen, setMenuOpen] = useState(false);
  const [sortBy, setSortBy] = useState<SortKey>(defaultSort);
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const listRef = useRef<HTMLDivElement>(null);

  const toggleSort = (col: SortKey) => {
    if (col === sortBy) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(col);
      setDir(col === 'name' ? 'asc' : 'desc'); // números: mayor→menor; nombre: A→Z
    }
  };

  const numFor = (it: Item) => (sortBy === 'count' ? count(it) : primary(it));
  const filtered = useMemo(() => {
    const base = q ? data.filter((d) => d.name.toLowerCase().includes(q.toLowerCase())) : data;
    const f = dir === 'asc' ? 1 : -1;
    return [...base].sort((a, b) =>
      sortBy === 'name' ? a.name.localeCompare(b.name) * f : (numFor(a) - numFor(b)) * f,
    );
  }, [data, q, sortBy, dir]); // eslint-disable-line react-hooks/exhaustive-deps
  const shown = limit > 0 ? filtered.slice(0, limit) : filtered;
  const ranked = sortBy === defaultSort && dir === 'desc'; // medallas solo en el ranking real
  const barVal = (it: Item) => (sortBy === 'name' ? primary(it) : numFor(it));
  const max = Math.max(1, ...filtered.map(barVal));
  const fname = (title || 'ranking').replace(/\s+/g, '_').toLowerCase();

  const SORTS: { key: SortKey; label: string }[] = isProd
    ? [{ key: 'count', label: 'Pzas' }, { key: 'value', label: '$' }, { key: 'name', label: 'A-Z' }]
    : [{ key: 'value', label: '$' }, { key: 'name', label: 'A-Z' }];

  const doExport = async (kind: 'excel' | 'pdf' | 'img') => {
    setMenuOpen(false);
    const countCol = isProd ? 'Piezas' : 'Órdenes';
    const avgCol = isProd ? 'Precio prom.' : 'Promedio';
    if (kind === 'excel') {
      await exportExcel(
        fname,
        filtered.map((r, i) => ({
          '#': i + 1,
          Nombre: r.name,
          [countCol]: count(r),
          Total: primary(r),
          [avgCol]: count(r) ? Math.round(primary(r) / count(r)) : 0,
        })),
      );
    } else if (kind === 'pdf') {
      await exportPdf(
        fname,
        title || 'Ranking',
        ['#', 'Nombre', countCol, 'Total', 'Prom.'],
        filtered.map((r, i) => [i + 1, r.name, count(r), money(primary(r)), money(count(r) ? primary(r) / count(r) : 0)]),
      );
    } else if (listRef.current) {
      await exportImage(listRef.current, fname);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="no-drag mb-2 flex items-center gap-1.5">
        <div className="flex flex-1 items-center gap-1 rounded-lg border border-slate-200 px-2 py-1">
          <Search className="h-3.5 w-3.5 shrink-0 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar…"
            className="w-full bg-transparent text-xs outline-none"
          />
        </div>
        <div className="flex shrink-0 rounded-lg bg-slate-100 p-0.5 text-[11px] font-semibold">
          {SORTS.map((s) => (
            <button
              key={s.key}
              onClick={() => toggleSort(s.key)}
              title={`Ordenar por ${s.label}`}
              className={`rounded px-1.5 py-0.5 ${sortBy === s.key ? 'bg-red-600 text-white' : 'text-slate-500 hover:text-slate-700'}`}
            >
              {s.label}{sortBy === s.key ? (dir === 'asc' ? ' ↑' : ' ↓') : ''}
            </button>
          ))}
        </div>
        <div className="flex shrink-0 rounded-lg bg-slate-100 p-0.5 text-[11px] font-semibold">
          {SHOW.map((n) => (
            <button
              key={n}
              onClick={() => setLimit(n)}
              className={`rounded px-1.5 py-0.5 ${limit === n ? 'bg-red-600 text-white' : 'text-slate-500 hover:text-slate-700'}`}
            >
              {n === 0 ? 'Todos' : n}
            </button>
          ))}
        </div>
        <div className="relative shrink-0">
          <button
            onClick={() => setMenuOpen((o) => !o)}
            title="Descargar"
            className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50"
          >
            <Download className="h-3.5 w-3.5" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 z-30 mt-1 w-28 overflow-hidden rounded-lg border border-slate-200 bg-white text-xs shadow-lg">
              <button onClick={() => doExport('img')} className="block w-full px-3 py-1.5 text-left hover:bg-slate-50">Imagen</button>
              <button onClick={() => doExport('pdf')} className="block w-full px-3 py-1.5 text-left hover:bg-slate-50">PDF</button>
              <button onClick={() => doExport('excel')} className="block w-full px-3 py-1.5 text-left hover:bg-slate-50">Excel</button>
            </div>
          )}
        </div>
      </div>

      <div ref={listRef} className="min-h-0 flex-1 space-y-2.5 overflow-auto pr-1">
        {shown.map((r, i) => (
          <div
            key={r.id ?? i}
            onClick={onRowDrill && !isProd ? () => onRowDrill(r as RankItem) : undefined}
            title={onRowDrill && !isProd ? 'Ver órdenes' : undefined}
            className={onRowDrill && !isProd ? 'no-drag -mx-1 cursor-pointer rounded-lg px-1 py-0.5 hover:bg-slate-50' : undefined}
          >
            <div className="flex items-center gap-2">
              <span
                className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-bold ${
                  ranked && i < 3 ? `bg-gradient-to-br ${MEDAL[i]} text-white shadow` : 'bg-slate-100 text-slate-500'
                }`}
              >
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-slate-800">{r.name}</p>
                <p className="text-[11px] text-slate-400">
                  {isProd ? units(count(r)) : `${count(r)} ${countLabel}`}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-sm font-bold tabular-nums text-red-700">{money(primary(r))}</p>
                <p className="text-[10px] text-slate-400">
                  {isProd ? `${money(count(r) ? primary(r) / count(r) : 0)}/pza` : `Prom. ${money(count(r) ? primary(r) / count(r) : 0)}`}
                </p>
              </div>
            </div>
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-gradient-to-r from-red-500 to-red-700"
                style={{ width: `${(barVal(r) / max) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
